/**
 * Round 98n (Jul 2026) — IPO Alert settings panel.
 *
 * Mirrors ExDivAlertSettings.jsx architecture but scoped to the
 * /ipo-watch page. Opens directly UNDER the "Alerts" pill button in
 * the page header (matches Calendar's disclosure-panel pattern).
 *
 * Backing endpoints (server.py):
 *   GET  /api/users/me/ipo-alert-prefs   → { prefs }
 *   POST /api/users/me/ipo-alert-prefs   → { ok, prefs, dismissals_cleared }
 *   GET  /api/ipo-alerts/upcoming         → { alerts, prefs, count }
 *
 * Any filter change (window / security_types / min_offer_size) wipes
 * the user's dismissed_tickers list server-side so the widened window
 * shows all matching IPOs fresh — per user spec: "unless they set
 * the settings to a new one ... it's still within that range".
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRing, BellOff, Loader2, Rocket } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOW_OPTIONS = [
  { value: 3, label: 'Last 3 days' },
  { value: 7, label: 'Last 7 days' },
  { value: 31, label: 'Last 1 month' },
];

const TYPE_OPTIONS = [
  { value: 'REIT', label: 'REIT', dot: 'bg-emerald-500' },
  { value: 'CEF', label: 'CEF', dot: 'bg-sky-500' },
  { value: 'BDC', label: 'BDC', dot: 'bg-violet-500' },
  { value: 'PFD', label: 'Preferred', dot: 'bg-amber-500' },
  { value: 'MLP', label: 'MLP', dot: 'bg-teal-500' },
  { value: 'SPAC', label: 'SPAC', dot: 'bg-rose-500' },
  { value: 'OTHER', label: 'Growth / Other', dot: 'bg-zinc-500' },
];

const OFFER_SIZE_OPTIONS = [
  { value: 0, label: 'Any size' },
  { value: 10_000_000, label: '≥ $10M' },
  { value: 50_000_000, label: '≥ $50M' },
  { value: 100_000_000, label: '≥ $100M' },
  { value: 250_000_000, label: '≥ $250M' },
];

// Live preview list — mirrors ExDivAlertSettings PreviewList.
function IpoPreviewList({ alerts, onOpenStock }) {
  if (!alerts?.length) {
    return (
      <div
        className="mt-2 rounded-md border border-dashed border-border bg-muted/20 p-2 text-[11px] text-muted-foreground font-semibold text-center"
        data-testid="ipo-settings-preview-empty"
      >
        No IPOs match your current filters. Try widening the window or clearing type filters.
      </div>
    );
  }
  const visible = alerts.slice(0, 5);
  return (
    <div className="mt-2 rounded-md border border-border bg-muted/10 overflow-hidden" data-testid="ipo-settings-preview-list">
      <table className="w-full text-[11px]" data-testid="ipo-settings-preview-table">
        <thead className="bg-muted/30 border-b border-border">
          <tr>
            <th className="px-2 py-1 text-left text-[9px] uppercase tracking-wide font-bold text-muted-foreground w-[26%]">Ticker</th>
            <th className="px-2 py-1 text-left text-[9px] uppercase tracking-wide font-bold text-muted-foreground w-[42%]">Company</th>
            <th className="px-2 py-1 text-center text-[9px] uppercase tracking-wide font-bold text-muted-foreground w-[16%]">Type</th>
            <th className="px-2 py-1 text-right text-[9px] uppercase tracking-wide font-bold text-muted-foreground w-[16%]">Listed</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {visible.map((a) => (
            <tr key={a.ticker} data-testid={`ipo-settings-preview-row-${a.ticker}`}>
              <td className="px-2 py-1.5 text-left font-mono">
                <button
                  type="button"
                  onClick={() => onOpenStock?.(a.ticker)}
                  className="font-bold text-violet-700 dark:text-violet-300 hover:underline focus:outline-none focus:ring-2 focus:ring-violet-500/40 rounded-sm"
                  title={`Open ${a.ticker}`}
                >
                  {a.ticker}
                </button>
              </td>
              <td className="px-2 py-1.5 text-left text-foreground truncate" title={a.issuer_name || ''}>
                {a.issuer_name || '—'}
              </td>
              <td className="px-2 py-1.5 text-center">
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-border bg-card text-foreground">
                  {a.kind}
                </span>
                {a.is_upcoming && (
                  <span
                    className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-1 ring-amber-500/30"
                    title="Not yet listed"
                  >
                    Upcoming
                  </span>
                )}
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-muted-foreground whitespace-nowrap">
                {a.listing_date || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {alerts.length > 5 && (
        <div className="px-2 py-1 text-[10px] text-center text-muted-foreground bg-muted/20 border-t border-border">
          + {alerts.length - 5} more · view all on the IPO Watch table below
        </div>
      )}
    </div>
  );
}

export default function IpoAlertSettings({ onClose, onOpenStock }) {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState([]);

  const fetchPrefs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/users/me/ipo-alert-prefs`);
      setPrefs(data?.prefs || null);
    } catch {
      toast.error('Could not load IPO alert settings');
    }
  }, []);

  const fetchPreview = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ipo-alerts/upcoming`);
      setPreview(Array.isArray(data?.alerts) ? data.alerts : []);
    } catch {
      /* silent — preview is non-essential */
    }
  }, []);

  useEffect(() => { fetchPrefs(); fetchPreview(); }, [fetchPrefs, fetchPreview]);

  const save = useCallback(async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/users/me/ipo-alert-prefs`, patch);
      setPrefs(data?.prefs || null);
      // Any filter change on the backend wipes dismissals and can
      // change the preview list — refetch. Backend returns
      // `dismissals_cleared: true` in that case; we could show a
      // toast but it clutters — keep quiet unless we know they
      // actually had dismissals before.
      fetchPreview();
      // Also nudge the bell to re-fetch so its IPO section reflects
      // the new filters immediately (no wait for 5-min poll).
      window.dispatchEvent(new Event('ipo-alerts-prefs-changed'));
    } catch (e) {
      toast.error('Could not save IPO alert settings');
    } finally {
      setSaving(false);
    }
  }, [fetchPreview]);

  if (!prefs) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2" data-testid="ipo-settings-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading IPO alert settings…
      </div>
    );
  }

  const toggleType = (t) => {
    const cur = new Set(prefs.security_types || []);
    if (cur.has(t)) cur.delete(t); else cur.add(t);
    save({ security_types: [...cur] });
  };

  return (
    <div className="space-y-4" data-testid="ipo-alert-settings">
      <div className="flex items-center gap-2">
        <BellRing className="w-4 h-4 text-violet-600 dark:text-violet-400" />
        <h3 className="text-sm font-bold tracking-tight">IPO Watch Alerts</h3>
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
      </div>

      <p className="text-[11px] text-foreground leading-relaxed">
        <span className="font-bold block">
          Get notified in the bell 🔔 &amp; on your Portfolio page when new IPOs list.
        </span>
        <span className="font-bold block mt-1">
          Only shows IPOs listed within your chosen window.
        </span>
      </p>

      {/* Master enable / disable */}
      <div className="rounded-lg border border-border bg-card p-3">
        <label className="flex items-center justify-between gap-2 cursor-pointer">
          <span className="text-xs font-bold flex items-center gap-2">
            {prefs.enabled ? (
              <BellRing className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
            ) : (
              <BellOff className="w-3.5 h-3.5 text-muted-foreground" />
            )}
            IPO alerts enabled
          </span>
          <input
            type="checkbox"
            className="h-4 w-4 accent-violet-600 cursor-pointer"
            checked={!!prefs.enabled}
            onChange={(e) => save({ enabled: e.target.checked })}
            data-testid="ipo-settings-enabled"
          />
        </label>
      </div>

      {/* Time window (single-select) */}
      <div className="rounded-lg border border-border bg-card p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">
          Time window
        </p>
        <div className="flex flex-wrap gap-1.5" data-testid="ipo-settings-windows">
          {WINDOW_OPTIONS.map((w) => {
            const active = prefs.window_days === w.value;
            return (
              <button
                key={w.value}
                type="button"
                disabled={!prefs.enabled}
                onClick={() => save({ window_days: w.value })}
                className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors ${
                  !prefs.enabled
                    ? 'opacity-40 cursor-not-allowed border-border bg-muted text-muted-foreground'
                    : active
                      ? 'border-violet-500 bg-violet-600 text-white hover:bg-violet-700'
                      : 'border-border bg-card text-foreground hover:bg-muted'
                }`}
                data-testid={`ipo-settings-window-${w.value}d`}
              >
                {w.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Security-type filter (multi-select — empty = all) */}
      <div className="rounded-lg border border-border bg-card p-3 space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">
            Security types
          </p>
          <p className="text-[9px] text-muted-foreground italic">
            {prefs.security_types?.length ? `${prefs.security_types.length} selected` : 'All types'}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="ipo-settings-types">
          {TYPE_OPTIONS.map((t) => {
            const active = prefs.security_types?.includes(t.value);
            return (
              <button
                key={t.value}
                type="button"
                disabled={!prefs.enabled}
                onClick={() => toggleType(t.value)}
                className={`text-[11px] font-bold px-2 py-1 rounded-full border transition-colors flex items-center gap-1.5 ${
                  !prefs.enabled
                    ? 'opacity-40 cursor-not-allowed border-border bg-muted text-muted-foreground'
                    : active
                      ? 'border-violet-500 bg-violet-600 text-white hover:bg-violet-700'
                      : 'border-border bg-card text-foreground hover:bg-muted'
                }`}
                data-testid={`ipo-settings-type-${t.value}`}
                title={active ? `Currently alerting for ${t.label}` : `Also alert for ${t.label}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
                {t.label}
              </button>
            );
          })}
        </div>
        <p className="text-[9px] text-muted-foreground italic">
          None selected = alerts for every IPO type.
        </p>
      </div>

      {/* Min offer size */}
      <div className="rounded-lg border border-border bg-card p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">
          Minimum offer size
        </p>
        <div className="flex flex-wrap gap-1.5" data-testid="ipo-settings-offer-sizes">
          {OFFER_SIZE_OPTIONS.map((o) => {
            const active = prefs.min_offer_size_usd === o.value;
            return (
              <button
                key={o.value}
                type="button"
                disabled={!prefs.enabled}
                onClick={() => save({ min_offer_size_usd: o.value })}
                className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors ${
                  !prefs.enabled
                    ? 'opacity-40 cursor-not-allowed border-border bg-muted text-muted-foreground'
                    : active
                      ? 'border-violet-500 bg-violet-600 text-white hover:bg-violet-700'
                      : 'border-border bg-card text-foreground hover:bg-muted'
                }`}
                data-testid={`ipo-settings-offer-${o.value}`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Auto-add to Watchlist */}
      <div className="rounded-lg border border-border bg-card p-3">
        <label className="flex items-center justify-between gap-2 cursor-pointer">
          <span className="text-xs font-bold flex items-center gap-2">
            <Rocket className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
            Auto-add matching IPOs to my Watchlist
          </span>
          <input
            type="checkbox"
            className="h-4 w-4 accent-violet-600 cursor-pointer"
            checked={!!prefs.auto_add_to_watchlist}
            disabled={!prefs.enabled}
            onChange={(e) => save({ auto_add_to_watchlist: e.target.checked })}
            data-testid="ipo-settings-auto-add"
          />
        </label>
        <p className="text-[9px] text-muted-foreground italic mt-1">
          When on, every new IPO that matches your filters is added to your Watchlist automatically.
        </p>
      </div>

      {/* Round 98n (Jul 2026) — Include upcoming IPOs toggle. Massive
          returns pending IPOs (listing_date > today) via
          ipo_status='pending'. Off by default because most users
          want listed IPOs; opt-in for early-mover users who want a
          heads-up on the pipeline. */}
      <div className="rounded-lg border border-border bg-card p-3">
        <label className="flex items-center justify-between gap-2 cursor-pointer">
          <span className="text-xs font-bold flex items-center gap-2">
            <Rocket className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
            Include upcoming IPOs (next 30 days)
          </span>
          <input
            type="checkbox"
            className="h-4 w-4 accent-amber-600 cursor-pointer"
            checked={!!prefs.include_upcoming}
            disabled={!prefs.enabled}
            onChange={(e) => save({ include_upcoming: e.target.checked })}
            data-testid="ipo-settings-include-upcoming"
          />
        </label>
        <p className="text-[9px] text-muted-foreground italic mt-1">
          Also alert on IPOs that have been priced but haven&rsquo;t started trading yet — early heads-up on the pipeline.
        </p>
      </div>

      {/* Live preview */}
      {prefs.enabled && (
        <div className="rounded-lg border border-border bg-card p-3 space-y-2">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">
            Preview — matches for your filters
          </p>
          <IpoPreviewList alerts={preview} onOpenStock={onOpenStock} />
        </div>
      )}

      {onClose && (
        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-bold px-3 py-1.5 rounded-md bg-muted hover:bg-muted/70 transition-colors"
            data-testid="ipo-settings-close-btn"
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}
